import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

const CHECKLIST = [
  {
    title: "Professional credentials",
    description:
      "Look for membership of a professional body such as Propertymark (NAEA), The Guild of Property Professionals, or the National Association of Estate Agents. Members are bound by a code of conduct, carry client money protection insurance, and undertake regular training. Ask about their qualifications and redress scheme.",
  },
  {
    title: "Local market knowledge",
    description:
      "The best agents know your street, not just your postcode. Ask how many properties they have sold in your area in the past 12 months, what the average sale-to-asking price ratio was, and how long listings typically take to go under offer. An agent who can reel off recent comparable evidence is one who genuinely knows the market.",
  },
  {
    title: "Marketing quality",
    description:
      "Review the agent\u2019s current listings on Rightmove and Zoopla. Are the photos professional? Are there floorplans? Is there video content? Agents who invest in high-quality marketing, particularly video, consistently achieve more viewings and stronger offers. A listing with video receives twice as many enquiries on average.",
  },
  {
    title: "Fee transparency",
    description:
      "Ask for a clear written breakdown of fees before signing anything. Understand whether the fee is sole agency or multi-agency, what the tie-in period is, and whether there are any withdrawal charges. The cheapest agent is rarely the best value; focus on the net proceeds after fees, not the percentage alone.",
  },
  {
    title: "Communication style",
    description:
      "How quickly did the agent respond to your initial enquiry? Were they pushy or professional? During the sales process you will rely on regular updates, honest feedback after viewings, and clear advice during negotiations. Ask how often they will update you and through which channels (phone, email, portal app).",
  },
  {
    title: "Online reviews and reputation",
    description:
      "Check Google reviews, Trustpilot, and allAgents for genuine client feedback. Look for consistent themes rather than individual outliers. Pay attention to how the agent responds to negative reviews; a professional, measured response is a good sign. Ask the agent for references from recent sellers in your area.",
  },
  {
    title: "Viewings and negotiation approach",
    description:
      "Find out whether viewings are accompanied or unaccompanied. Accompanied viewings allow the agent to sell the lifestyle, answer questions, and gather genuine feedback. Ask about their negotiation strategy: how do they handle multiple offers? How do they qualify buyers before arranging viewings?",
  },
  {
    title: "Sale progression",
    description:
      "A good agent\u2019s job does not end when an offer is accepted. Ask how they manage the process from offer to completion. Do they have a dedicated sales progressor? How do they chase solicitors and keep the chain moving? Proactive sale progression is what separates the best agents from the rest.",
  },
]

const FAQ = [
  {
    q: "How many estate agents should I get valuations from?",
    a: "Get at least three valuations from different agents. This gives you a fair view of the market, lets you compare marketing approaches, and helps you spot agents who overvalue to win the instruction. Be wary of any agent whose valuation is significantly higher than the others without a clear explanation.",
  },
  {
    q: "Should I choose the agent with the highest valuation?",
    a: "Not necessarily. Some agents deliberately overvalue properties to win the instruction, then reduce the price after a few weeks on the market. This strategy wastes time and can damage your listing\u2019s appeal. Focus on comparable evidence and the agent\u2019s track record of achieved prices, not just the initial figure.",
  },
  {
    q: "What is the difference between an estate agent and a letting agent?",
    a: "An estate agent handles property sales, from marketing and viewings to offer negotiation and sale progression. A letting agent manages rental properties, finding tenants, handling references, and (optionally) managing the property on an ongoing basis. Many firms offer both services.",
  },
  {
    q: "Do I have to pay an estate agent if my house does not sell?",
    a: "In most standard agency agreements, you only pay the agent on completion of a sale. However, some contracts include minimum term clauses or withdrawal fees. Always read the contract carefully and check whether fees are payable on exchange or completion, and what happens if you withdraw the property.",
  },
  {
    q: "How important is video marketing when choosing an agent?",
    a: "Very. Properties with video receive twice as many enquiries and 73% of vendors say they prefer agents who offer video marketing. Video helps buyers visualise the property before booking a viewing, which means more qualified viewings and fewer time-wasters. It is also a strong signal that the agent invests in modern, effective marketing.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function HowToChooseEstateAgentGuide({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Choose an Estate Agent: 8-Point Checklist",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "How to Choose an Estate Agent" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            How to choose an estate agent
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Choosing the right estate agent is the single biggest decision
            you will make when selling your home. The wrong choice costs
            thousands in lower sale prices and months of wasted time. Here
            is how to find the right one.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Intro */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why your choice of agent matters
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Your estate agent controls how your property is priced,
              presented, and promoted to buyers. A strong agent will achieve
              a higher sale price, negotiate firmly on your behalf, and
              guide the transaction through to completion with minimal
              stress. A weak agent will leave money on the table.
            </p>
            <p>
              Research from the{" "}
              <a
                href="https://www.propertymark.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red underline decoration-red/30 transition-colors hover:text-deep-red"
              >
                Propertymark
              </a>{" "}
              industry body shows that properties marketed by qualified
              agents with strong marketing sell faster and closer to asking
              price. The difference between a good agent and a poor one can
              easily exceed five figures on a typical UK property.
            </p>
            <p>
              This guide walks you through the eight things you should
              evaluate before instructing an agent.
            </p>
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            The 8-point checklist
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            Evaluate every agent you speak to against these eight criteria.
          </p>

          <div className="mt-8 space-y-6">
            {CHECKLIST.map((item, index) => (
              <ScrollReveal key={item.title}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <div className="flex items-start gap-4">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red/10 font-display text-sm font-bold text-red">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold text-charcoal">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* What to ask section */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Questions to ask at a valuation
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              When an agent visits your property for a market appraisal,
              come prepared with questions. Their answers will tell you more
              than any brochure.
            </p>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-slate">
            {[
              "How many properties have you sold within half a mile of my home in the past year?",
              "What is your average sale-to-asking price ratio?",
              "What marketing do you include in your standard fee?",
              "Do you offer video marketing for property listings?",
              "How will you handle viewings, and will they be accompanied?",
              "What is your fee structure, and is there a tie-in period?",
              "How do you qualify buyers before arranging viewings?",
              "Who will be my main point of contact throughout the sale?",
              "What happens if the property does not sell within the first eight weeks?",
              "Can you provide references from recent sellers in my area?",
            ].map((question) => (
              <li key={question} className="flex gap-2">
                <span className="mt-0.5 text-red">&#8226;</span>
                <span>{question}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Red flags */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Red flags to watch for
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Not all agents have your best interests at heart. Watch out
              for these warning signs during the valuation process:
            </p>
          </div>
          <div className="mt-6 space-y-3">
            {[
              {
                flag: "A valuation significantly higher than competitors",
                detail:
                  "Overvaluing is the oldest trick in the book. The agent wins the instruction, then reduces the price after a few weeks. Your property looks stale on the portals and you end up selling for less.",
              },
              {
                flag: "Pressure to sign a long tie-in contract",
                detail:
                  "A standard sole agency agreement is 8-12 weeks. Anything longer than 16 weeks should raise questions. Avoid agents who insist on long lock-in periods with steep withdrawal fees.",
              },
              {
                flag: "No mention of marketing strategy",
                detail:
                  "If the agent talks only about price and fee without discussing how they will market your property, that is a concern. The marketing plan is what drives viewings and offers.",
              },
              {
                flag: "Poor-quality existing listings",
                detail:
                  "Check their current listings on Rightmove and Zoopla. If you see dark photos, no floorplans, and generic descriptions, expect the same treatment for your property.",
              },
              {
                flag: "No sale progression plan",
                detail:
                  "Ask what happens after an offer is accepted. If the answer is vague, you may find yourself chasing solicitors and managing the chain on your own.",
              },
            ].map((item) => (
              <div
                key={item.flag}
                className="rounded-xl border border-stone bg-white p-5"
              >
                <h3 className="text-sm font-semibold text-charcoal">
                  {item.flag}
                </h3>
                <p className="mt-1.5 text-sm text-slate">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            {
              stat: "3+",
              label: "valuations recommended before choosing",
            },
            { stat: "73%", label: "of vendors prefer agents who offer video" },
            {
              stat: "2x",
              label: "more enquiries on listings with video",
            },
            {
              stat: "40%",
              label: "of vendors say marketing influenced their choice",
            },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                href: "/guides/estate-agent-fees",
                title: "Estate agent fees",
                desc: "Understand what agents charge and how to negotiate a fair deal.",
              },
              {
                href: "/directory",
                title: "Agent directory",
                desc: "Compare estate agents in your area side by side.",
              },
              {
                href: "/tools/commission-calculator",
                title: "Commission calculator",
                desc: "See exactly how much different fee levels cost you.",
              },
              {
                href: "/guides/selling-your-house",
                title: "Selling your house",
                desc: "The full step-by-step guide to selling in the UK.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            The best agents market with video
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            When you are comparing agents, ask whether they offer property
            video. The ones who do are the ones investing in results.
            QuiteRight makes it easy for any agent to create stunning video
            ads in under 60 seconds.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/examples"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See video examples
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
